import React, { useState, useEffect } from 'react';
import { Button, Card, Badge } from 'react-bootstrap';
import ExerciseLibrary from './components/ExerciseLibrary';
import WorkoutLogger from './components/WorkoutLogger';
import TemplateManager from './components/TemplateManager';
import ProgramManager from './components/ProgramManager';
import ActiveProgramCard from './components/ActiveProgramCard';
import ErrorBoundary from './components/ErrorBoundary';
import { useWorkoutDraft } from './hooks/useWorkoutDraft';
import { workoutAPI } from './services/workoutAPI';
import './styles/WorkoutTracker.scss';

function WorkoutTracker() {
  const [view, setView] = useState('landing');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedProgramWorkout, setSelectedProgramWorkout] = useState(null);
  const [activePrograms, setActivePrograms] = useState([]);
  const { hasDraft, draftAge, checkDraft } = useWorkoutDraft();

  // Check for draft and fetch active programs when landing page loads
  useEffect(() => {
    checkDraft();
    fetchActivePrograms();
  }, [checkDraft]);

  console.log('🏠 Landing page - Draft status:', { hasDraft, draftAge });

  const fetchActivePrograms = async () => {
    try {
      const data = await workoutAPI.fetchActivePrograms();
      setActivePrograms(data);
    } catch (err) {
      console.error('Error fetching active programs:', err);
    }
  };

  const handleContinueProgram = async (activeProgram) => {
    try {
      const nextWorkout = await workoutAPI.fetchNextWorkout(activeProgram.program_id);
      if (nextWorkout) {
        // Load template details
        const template = await workoutAPI.fetchTemplateById(nextWorkout.template_id);

        setSelectedProgramWorkout({
          active_program_id: activeProgram.id,
          program_id: activeProgram.program_id,
          program_name: activeProgram.program_name,
          program_workout_id: nextWorkout.id,
          template_id: nextWorkout.template_id,
          template: template,
          name: nextWorkout.name,
          phase: nextWorkout.phase,
          week: nextWorkout.week,
          day: nextWorkout.day
        });
        setView('logger');
      }
    } catch (err) {
      console.error('Error loading next workout:', err);
    }
  };

  const handleViewProgram = (programId) => {
    setView('programs');
  };

  const renderLandingPage = () => {
    return (
      <div className="workout-tracker-landing">
        <div className="text-center mb-5">
          <h1 className="display-4">
            <i className="fas fa-dumbbell me-3"></i>
            Workout Tracker
          </h1>
          <p className="lead text-muted">Track your workouts, build programs, and monitor your progress</p>
        </div>

        {/* Active Programs Section */}
        {activePrograms.length > 0 && (
          <div className="active-programs-section mb-5">
            <h3 className="mb-3">
              <i className="fas fa-fire me-2 text-success"></i>
              Active Programs
            </h3>
            <div className="row g-3">
              {activePrograms.map(ap => (
                <div className="col-md-6" key={ap.id}>
                  <ActiveProgramCard
                    activeProgram={ap}
                    onContinue={handleContinueProgram}
                    onViewProgram={handleViewProgram}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="row g-4">
          {/* Start New Workout */}
          <div className="col-md-6">
            <Card className="h-100 workout-card">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <i className="fas fa-play-circle fa-4x text-success"></i>
                  {hasDraft && (
                    <Badge bg="warning" className="mt-2 d-block">
                      <i className="fas fa-save me-1"></i>
                      Draft Saved
                    </Badge>
                  )}
                </div>
                <Card.Title className="text-center mb-3">Start New Workout</Card.Title>
                <Card.Text className="text-center text-muted flex-grow-1">
                  {hasDraft
                    ? `Resume your workout from ${draftAge}`
                    : 'Begin a new workout session and log your exercises'
                  }
                </Card.Text>
                <Button
                  variant="success"
                  size="lg"
                  className="mt-3"
                  onClick={() => setView('logger')}
                >
                  <i className={`fas ${hasDraft ? 'fa-redo' : 'fa-play'} me-2`}></i>
                  {hasDraft ? 'Resume Workout' : 'Start Workout'}
                </Button>
              </Card.Body>
            </Card>
          </div>

          {/* Start from Template */}
          <div className="col-md-6">
            <Card className="h-100 workout-card">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <i className="fas fa-file-alt fa-4x text-primary"></i>
                </div>
                <Card.Title className="text-center mb-3">Start from Template</Card.Title>
                <Card.Text className="text-center text-muted flex-grow-1">
                  Choose a workout template and begin your session
                </Card.Text>
                <Button
                  variant="primary"
                  size="lg"
                  className="mt-3"
                  onClick={() => setView('templates')}
                >
                  <i className="fas fa-copy me-2"></i>
                  View Templates
                </Button>
              </Card.Body>
            </Card>
          </div>

          {/* Manage Exercises */}
          <div className="col-md-6">
            <Card className="h-100 workout-card">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <i className="fas fa-list fa-4x text-info"></i>
                </div>
                <Card.Title className="text-center mb-3">Manage Exercises</Card.Title>
                <Card.Text className="text-center text-muted flex-grow-1">
                  Create and organize your exercise library
                </Card.Text>
                <Button
                  variant="info"
                  size="lg"
                  className="mt-3"
                  onClick={() => setView('exercises')}
                >
                  <i className="fas fa-dumbbell me-2"></i>
                  Exercise Library
                </Button>
              </Card.Body>
            </Card>
          </div>

          {/* Programs */}
          <div className="col-md-6">
            <Card className="h-100 workout-card">
              <Card.Body className="d-flex flex-column">
                <div className="text-center mb-3">
                  <i className="fas fa-calendar-week fa-4x" style={{ color: '#9b59b6' }}></i>
                </div>
                <Card.Title className="text-center mb-3">Programs</Card.Title>
                <Card.Text className="text-center text-muted flex-grow-1">
                  Create and manage multi-week training programs
                </Card.Text>
                <Button
                  style={{ backgroundColor: '#9b59b6', borderColor: '#9b59b6' }}
                  size="lg"
                  className="mt-3"
                  onClick={() => setView('programs')}
                >
                  <i className="fas fa-calendar-alt me-2"></i>
                  View Programs
                </Button>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Quick Stats - Placeholder for future */}
        <div className="row mt-5">
          <div className="col-12">
            <Card>
              <Card.Body>
                <h5 className="mb-3">
                  <i className="fas fa-history me-2"></i>
                  Recent Activity
                </h5>
                <p className="text-muted text-center py-4">
                  Your workout history will appear here once you start logging workouts
                </p>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <div className="workout-tracker-container">
        {view === 'landing' && renderLandingPage()}
        {view === 'exercises' && <ExerciseLibrary onBack={() => setView('landing')} />}
        {view === 'templates' && (
          <TemplateManager
            onBack={() => setView('landing')}
            onStartWorkout={(template) => {
              setSelectedTemplate(template);
              setView('logger');
            }}
          />
        )}
        {view === 'programs' && (
          <ProgramManager
            onBack={() => setView('landing')}
          />
        )}
        {view === 'logger' && (
          <WorkoutLogger
            template={selectedTemplate}
            programWorkout={selectedProgramWorkout}
            onFinish={() => {
              setSelectedTemplate(null);
              setSelectedProgramWorkout(null);
              setView('landing');
              fetchActivePrograms(); // Refresh active programs
            }}
            onCancel={() => {
              setSelectedTemplate(null);
              setSelectedProgramWorkout(null);
              setView('landing');
            }}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}

export default WorkoutTracker;
